import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import logoSvg from '@/assets/logo.svg'
import { FxAlert, FxButton, FxIcon, FxSpinner } from '@/components/fx'
import { ApiError, publicUploadApi } from '@/lib/api'
import type { UploadLinkInfo } from '@/types/domain'
import { CaptureQueueItem } from './CaptureQueueItem'
import {
  CAPTURE_ACCEPT,
  LINK_GONE_MESSAGE,
  classifyUploadError,
  newCaptureItem,
  sendButtonLabel,
  sendableItems,
  type CaptureItem,
} from './uploadQueue'

/**
 * Capture: a phone, a stack of paper, a token link, no account.
 *
 * `GET|POST /public/upload/:token` carries no JWT — this screen must never
 * touch the session client, must never ask for a login, and must keep
 * working when a whole drawer is photographed in a row. The camera is offered
 * through a plain file input with `capture="environment"`, which opens the
 * native camera on both iOS and Android and, unlike getUserMedia, needs no
 * permission prompt of its own and gives full sensor resolution.
 *
 * The person shoots first and sends afterwards: photographing twenty pages
 * with a send between each is twenty chances to lose the rhythm, and a queue
 * also makes the failures reviewable rather than transient.
 */
export function CapturePage() {
  const { token = '' } = useParams()
  const [info, setInfo] = useState<UploadLinkInfo | null>(null)
  const [loadError, setLoadError] = useState<{ message: string; gone: boolean } | null>(null)
  const [items, setItems] = useState<CaptureItem[]>([])
  const [sending, setSending] = useState(false)
  const [banner, setBanner] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const cameraInput = useRef<HTMLInputElement>(null)
  const libraryInput = useRef<HTMLInputElement>(null)

  const load = useCallback(() => {
    setLoadError(null)
    void publicUploadApi
      .info(token)
      .then(setInfo)
      .catch((cause) => {
        const api = ApiError.from(cause)
        setLoadError(
          api.isNotFound
            ? { message: LINK_GONE_MESSAGE, gone: true }
            : { message: api.message, gone: false },
        )
      })
  }, [token])

  useEffect(load, [load])

  const maxSizeMb = info?.maxSizeMb ?? 0
  const pending = sendableItems(items)

  // Nothing in this queue is on the server yet and the File handles cannot be
  // persisted, so leaving the page really does discard the photos. The browser
  // is the only thing that can warn in time.
  useEffect(() => {
    if (pending.length === 0) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Safari — which is what a phone photographing a filing cabinet runs —
      // acts on `returnValue`, not on `preventDefault` alone. The sentence on
      // screen is still the real safeguard.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [pending.length])

  // A phone that locks its screen mid-batch suspends the tab and the upload
  // in flight with it. The wake lock is best-effort: an unsupported browser
  // and a refused request both just mean the person keeps the screen awake by
  // hand, which is what they do today.
  useEffect(() => {
    if (!sending) return
    let lock: WakeLockSentinel | null = null
    let released = false
    void navigator.wakeLock
      ?.request('screen')
      .then((sentinel) => {
        if (released) void sentinel.release()
        else lock = sentinel
      })
      .catch(() => undefined)
    return () => {
      released = true
      void lock?.release()
    }
  }, [sending])

  const itemsRef = useRef(items)
  itemsRef.current = items
  useEffect(() => {
    // Only on unmount; an individual removal revokes its own URL as it goes.
    return () => {
      itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl))
    }
  }, [])

  function patch(id: string, changes: Partial<CaptureItem>) {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)))
  }

  function ingest(files: FileList | null) {
    if (!files || files.length === 0) return
    const added = Array.from(files).map((file) =>
      newCaptureItem(file, maxSizeMb, { previewUrl: URL.createObjectURL(file) }),
    )
    setItems((current) => [...current, ...added])
    setDone(false)
  }

  function remove(item: CaptureItem) {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
    setItems((current) => current.filter((entry) => entry.id !== item.id))
  }

  /** One upload. Returns the failure so the batch can decide whether to go on. */
  async function sendOne(item: CaptureItem) {
    patch(item.id, { status: 'sending', progress: 0, error: undefined })
    try {
      await publicUploadApi.upload(token, item.file, item.file.name, (loaded, total) => {
        if (total > 0) patch(item.id, { progress: loaded / total })
      })
      patch(item.id, { status: 'sent', progress: 1 })
      return null
    } catch (cause) {
      const failure = classifyUploadError(cause)
      // A rate limit is not this photo's fault, so it goes back to `ready`
      // and can be sent unchanged once the hour is up.
      patch(item.id, {
        status: failure.kind === 'item' ? 'error' : 'ready',
        progress: 0,
        error: failure.kind === 'item' ? failure.message : undefined,
      })
      return failure
    }
  }

  /**
   * Sequential on purpose: the gateway caps concurrent uploads per instance
   * and answers 503 past the cap, so a drop of twenty photos fired at once
   * loses some of them.
   */
  async function sendAll() {
    const queue = sendableItems(items)
    if (queue.length === 0 || sending) return
    setSending(true)
    setBanner(null)
    let failed = false
    for (const item of queue) {
      // The queue is a snapshot: an upload takes seconds, and every row still
      // carries its X button while another one is sending. A photo taken out
      // of the list must not be sent anyway — it would arrive in the cockpit
      // and spend one of the 60 uploads an hour the token allows, with
      // nothing on screen to show for it.
      if (!itemsRef.current.some((entry) => entry.id === item.id)) continue
      const failure = await sendOne(item)
      if (!failure) continue
      failed = true
      if (failure.kind === 'rateLimit') {
        setBanner(failure.message)
        break
      }
      if (failure.kind === 'linkGone') {
        setLoadError({ message: failure.message, gone: true })
        break
      }
    }
    setSending(false)
    if (!failed) setDone(true)
  }

  async function retry(item: CaptureItem) {
    if (sending) return
    setSending(true)
    setBanner(null)
    const failure = await sendOne(item)
    if (failure?.kind === 'rateLimit') setBanner(failure.message)
    if (failure?.kind === 'linkGone') setLoadError({ message: failure.message, gone: true })
    // A retry that clears the last outstanding item finishes the batch, so it
    // reaches the same success view a clean run does. `items` still holds the
    // pre-send state, hence the exception for the item just sent.
    if (!failure && items.every((entry) => entry.id === item.id || entry.status === 'sent')) {
      setDone(true)
    }
    setSending(false)
  }

  if (loadError) {
    return (
      <Frame>
        <FxAlert tone={loadError.gone ? 'warning' : 'danger'} title="Kein Upload möglich">
          {loadError.message}
        </FxAlert>
        {!loadError.gone && (
          <FxButton variant="tonal" icon="refresh" onClick={load}>
            Erneut versuchen
          </FxButton>
        )}
      </Frame>
    )
  }

  if (!info) {
    return (
      <Frame>
        <div className="grid flex-1 place-items-center">
          <FxSpinner label="Link wird geprüft …" />
        </div>
      </Frame>
    )
  }

  if (done) {
    return (
      <Frame>
        <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
          <FxIcon name="check_circle" size={48} className="text-success-text" />
          <p className="text-body">
            Gesendet – die Aufnahmen werden jetzt analysiert und erscheinen im Cockpit.
          </p>
          <FxButton
            className="h-14 px-8"
            icon="photo_camera"
            onClick={() => {
              items.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl))
              setItems([])
              setDone(false)
            }}
          >
            Weitere hinzufügen
          </FxButton>
        </div>
      </Frame>
    )
  }

  return (
    <Frame>
      <div className="text-center">
        <p className="text-body">Formulare hinzufügen zu</p>
        <h1 className="text-panel-title [overflow-wrap:anywhere]">{info.templateTitle}</h1>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input
          ref={cameraInput}
          type="file"
          accept={CAPTURE_ACCEPT}
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            ingest(event.target.files)
            // Cleared so a second page the camera names identically still
            // fires a change event.
            event.target.value = ''
          }}
        />
        <input
          ref={libraryInput}
          type="file"
          accept={CAPTURE_ACCEPT}
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            ingest(event.target.files)
            event.target.value = ''
          }}
        />
        <FxButton
          className="h-14"
          icon="photo_camera"
          disabled={sending}
          onClick={() => cameraInput.current?.click()}
        >
          Foto aufnehmen
        </FxButton>
        <FxButton
          variant="tonal"
          className="h-14"
          icon="photo_library"
          disabled={sending}
          onClick={() => libraryInput.current?.click()}
        >
          Aus Fotos wählen
        </FxButton>
      </div>

      {banner && <FxAlert tone="warning">{banner}</FxAlert>}

      <h2 className="text-panel-title">Aufgenommen ({items.length})</h2>

      {items.length === 0 ? (
        <p className="text-small text-text-muted">
          Seite für Seite abfotografieren: gerade von oben, das ganze Blatt im Bild. Die Aufnahmen
          werden erst gesendet, wenn Sie unten darauf tippen.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item, index) => (
            <CaptureQueueItem
              key={item.id}
              item={item}
              index={index}
              maxSizeMb={maxSizeMb}
              onRetry={(entry) => void retry(entry)}
              onRemove={remove}
            />
          ))}
        </ul>
      )}

      <div className="flex-1" />

      {pending.length > 0 && (
        <p className="text-caption text-text-muted">
          Noch nicht gesendete Aufnahmen gehen verloren, wenn diese Seite geschlossen wird.
        </p>
      )}

      <FxButton
        block
        className="h-14"
        disabled={pending.length === 0 || sending}
        onClick={() => void sendAll()}
      >
        {sending ? 'Wird gesendet …' : sendButtonLabel(pending.length)}
      </FxButton>
    </Frame>
  )
}

/**
 * The phone frame. Centred and capped rather than full-bleed, because the
 * link is just as often opened on the laptop that generated the QR code, and
 * a 1440px-wide camera button reads as a broken page.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-[100dvh] w-full max-w-[480px] flex-col gap-5 bg-surface px-5 pb-6 pt-7">
      <img src={logoSvg} alt="formalogix" width={48} height={48} className="self-center" />
      {children}
    </main>
  )
}
